import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { ColorChip, ColorPicker } from "@/components/online/ColorPicker";
import { FieldError, INPUT_CLASS, OnlineShell, Panel } from "@/components/online/OnlineShell";
import { useLobby } from "@/hooks/useLobby";
import { clearOnline, errorText, getOnline, joinLobby, PLAYER_COLOR_ORDER, reclaimSeat, saveOnline } from "@/lib/api";

function JoinForm({ lobby }) {
  const navigate = useNavigate();
  const taken = lobby.members.map((m) => m.color);
  const full = lobby.members.length >= 4;
  const [name, setName] = useState("");
  const [color, setColor] = useState(() => PLAYER_COLOR_ORDER.find((c) => !taken.includes(c)) || "red");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return setError("Pick a name");
    setBusy(true);
    setError("");
    try {
      const { ticket } = await joinLobby(lobby.code, name.trim(), color);
      saveOnline({ code: lobby.code, ticket, game_id: null });
      navigate(`/lobby/${lobby.code}`);
    } catch (err) {
      setError(errorText(err, "Could not join"));
      setBusy(false);
    }
  };

  return (
    <Panel testId="join-form-panel">
      <form onSubmit={submit}>
        <div className="label-kicker text-[color:var(--ink-3)] mb-4">{`${lobby.members.length} of 4 in the lobby`}</div>
        <Input data-testid="join-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" maxLength={24} className={INPUT_CLASS} />
        <div className="mt-5">
          <ColorPicker value={color} taken={taken} onChange={setColor} />
        </div>
        <FieldError testId="join-error">{full ? "Lobby is full" : error}</FieldError>
        <div className="mt-6 flex justify-end">
          <button type="submit" disabled={busy || full} className="btn-tactile btn-tactile--primary" data-testid="join-submit-btn">
            {busy ? "Joining…" : "Join lobby →"}
          </button>
        </div>
      </form>
    </Panel>
  );
}

function SeatList({ lobby }) {
  const navigate = useNavigate();
  const [busySeat, setBusySeat] = useState(null);
  const [error, setError] = useState("");

  const take = async (seat) => {
    setBusySeat(seat);
    setError("");
    try {
      const { ticket } = await reclaimSeat(lobby.code, seat);
      saveOnline({ code: lobby.code, ticket, game_id: lobby.game_id });
      navigate(`/game/${lobby.game_id}`);
    } catch (err) {
      setError(errorText(err, "Could not take that seat"));
      setBusySeat(null);
    }
  };

  if (!lobby.game_exists) return <Panel testId="seat-list-panel"><p data-testid="game-ended-msg" className="text-[color:var(--ink-3)]">This game has ended</p></Panel>;

  return (
    <Panel testId="seat-list-panel">
      <p data-testid="game-started-msg" className="text-[color:var(--ink-2)] font-semibold mb-4">This game has started — pick your seat to rejoin</p>
      {lobby.my_seat !== null && lobby.my_seat !== undefined && (
        <button type="button" className="btn-tactile btn-tactile--primary mb-4" onClick={() => navigate(`/game/${lobby.game_id}`)} data-testid="seat-list-back-to-game-btn">
          Back to your game
        </button>
      )}
      <div className="space-y-2">
        {lobby.members.map((m, i) => (
          <button
            key={i}
            type="button"
            disabled={!m.reclaimable || busySeat !== null}
            onClick={() => take(i)}
            data-testid={`reclaim-seat-${i}`}
            className="w-full flex items-center justify-between gap-4 p-3 border border-[color:var(--line)] rounded-[8px] text-left disabled:cursor-not-allowed"
            style={{ background: "var(--card)", opacity: m.reclaimable ? 1 : 0.55, transition: "background-color 150ms ease" }}
          >
            <span className="flex items-center gap-3">
              <ColorChip color={m.color} label="" />
              <span className="font-semibold text-[color:var(--ink)]">{`I'm ${m.name}`}</span>
            </span>
            <span className="text-xs text-[color:var(--ink-3)]">{busySeat === i ? "Taking seat…" : m.reclaimable ? "Tap to take this seat" : "Connected"}</span>
          </button>
        ))}
      </div>
      <FieldError testId="reclaim-error">{error}</FieldError>
    </Panel>
  );
}

export default function JoinScreen() {
  const code = useParams().code.toUpperCase();
  const navigate = useNavigate();
  const { lobby, status } = useLobby(code);

  useEffect(() => {
    if (!lobby) return;
    const mine = lobby.my_seat !== null && lobby.my_seat !== undefined;
    if (mine && lobby.status === "waiting") navigate(`/lobby/${code}`, { replace: true });
    else if (!mine && getOnline()?.code === code) clearOnline();
  }, [lobby, code, navigate]);

  return (
    <OnlineShell testId="join-screen" kicker={`Lobby ${code}`} title="Join the game" back="/online" backLabel="Play Online">
      {status === "missing" && <Panel testId="join-missing-panel"><p data-testid="lobby-not-found-msg" className="text-[color:var(--ink-3)]">Lobby not found</p></Panel>}
      {status === "offline" && !lobby && <p className="text-sm text-[color:var(--ink-3)]" data-testid="join-reconnecting">Reconnecting…</p>}
      {status !== "missing" && lobby && (lobby.status === "waiting" ? <JoinForm lobby={lobby} /> : <SeatList lobby={lobby} />)}
    </OnlineShell>
  );
}
